import React, { useState, useEffect } from 'react';
import ComplaintService from '../services/complaint.service';
import '../styles/PreviousComplaintsModal.css';

interface PreviousComplaintsProps {
  categoryId: number | null;
  onClose: () => void;
  categoryName: string;
}

interface Complaint {
  id: string;
  subject: string;
  description: string;
  status: 'Pending' | 'Ongoing' | 'Completed';
  created_at: string;
  photo_url?: string;
}

const PreviousComplaintsModal: React.FC<PreviousComplaintsProps> = ({ categoryId, onClose, categoryName }) => {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (categoryId) {
      fetchComplaints();
    }
  }, [categoryId]);

  const fetchComplaints = async () => {
    if (!categoryId) return;
    
    setLoading(true);
    try {
      const response = await ComplaintService.getCategoryComplaintsAllUsers(categoryId);
      setComplaints(response.data);
    } catch (err: any) {
      setError('Failed to load previous complaints');
      console.error('Error loading complaints:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'badge bg-warning text-dark';
      case 'Ongoing':
        return 'badge bg-primary';
      case 'Completed':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content previous-complaints-modal">
        <div className="modal-header">
          <h5 className="modal-title">Previous Complaints for {categoryName}</h5>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : complaints.length === 0 ? (
            <div className="alert alert-info">
              No previous complaints found for this category.
            </div>
          ) : (
            <div className="previous-complaints-list">
              {complaints.map((complaint) => (
                <div key={complaint.id} className="card mb-3">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">{complaint.subject}</h6>
                    <span className={getStatusBadgeClass(complaint.status)}>
                      {complaint.status}
                    </span>
                  </div>
                  <div className="card-body">
                    <p className="card-text">{complaint.description}</p>
                    <div className="text-muted small">
                      Submitted on: {formatDate(complaint.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default PreviousComplaintsModal;
